/**
 * Road closures, entered by the county.
 *
 * ## Why this is operator-entered and not a feed
 *
 * ARDOT's IDriveArkansas has this data, but two things rule it out. Its acceptable-use
 * policy withholds authorisation from third-party application developers, and — the
 * decisive one — **ARDOT covers state highways only**. The county road under four feet
 * of water is in nobody's feed. The deputy who put the barricade there is the
 * authoritative source, so this models what they know.
 *
 * Arkansas publishes no open alternative: it is absent from the federal WZDx feed
 * registry, and the Arkansas GIS Office transportation services are static 2005 TIGER
 * reference data.
 */
import { pointToPathMiles, haversineMiles } from './eta.js';
/** Why the road is shut. `other` exists so nothing has to be misfiled. */
export const CLOSURE_REASONS = [
    'flood',
    'ice',
    'snow',
    'debris',
    'crash',
    'construction',
    'other',
];
export const CLOSURE_REASON_LABELS = {
    flood: 'High water',
    ice: 'Ice',
    snow: 'Snow',
    debris: 'Debris / trees down',
    crash: 'Crash',
    construction: 'Construction',
    other: 'Other',
};
/**
 * How bad it is, worst first. This is the "road surface condition" half of the ask:
 * "US-64 from the river to the county line — ice, passable with care" is
 * `reason: 'ice', condition: 'hazardous'`.
 */
export const CLOSURE_CONDITIONS = ['closed', 'restricted', 'hazardous'];
export const CLOSURE_CONDITION_LABELS = {
    closed: 'Closed',
    restricted: 'Restricted',
    hazardous: 'Passable with care',
};
export const CLOSURE_CONDITION_HELP = {
    closed: 'Barricaded. Do not travel.',
    restricted: 'One lane, local traffic, or high clearance only.',
    hazardous: 'Open, but water or ice on the road.',
};
/**
 * Colour of the line drawn along a closed stretch of road.
 *
 * Red for closed, and this deliberately overrides the rule that annotations avoid the
 * hazard palette. Two reasons. Red is what a closed road is marked with in the physical
 * world, so it needs no legend. And the original worry — confusing an annotation with a
 * warning AREA — does not apply to a 4px line: a tornado polygon is a translucent fill
 * many miles across, a closure is a hairline following a road.
 *
 * These are tuned for the PALE basemap, which is the only one this app draws on. The
 * previous values were greys chosen for a dark surface and were effectively invisible.
 */
export const CLOSURE_COLORS = {
    closed: '#d81d2c',
    restricted: '#f26a1b',
    hazardous: '#e8a800',
};
/**
 * The casing drawn under the line.
 *
 * DARK, not white. A white casing was the original bug: on a near-white basemap it is
 * camouflage, so the closure read as a faint smudge across the roads instead of a line
 * anybody would notice. A dark casing gives the coloured line a hard edge against both
 * the pale background and the orange highway fills underneath it.
 */
export const CLOSURE_CASING = '#1a1208';
/**
 * Colour of the cone marker dropped at each closure.
 *
 * Cone orange, and yes that is close to the severe-thunderstorm hue this codebase
 * otherwise reserves for NWS polygons. The rule it bends — no annotation wears a hazard
 * colour — exists so a drawn AREA is never mistaken for a warning area. A 30px cone
 * glyph on a dark badge is not an area and cannot be misread as one; its shape carries
 * the meaning, and the colour only has to make it findable on a pale basemap.
 *
 * The greys in CLOSURE_COLORS above were chosen for a dark surface and are right for
 * the LINE drawn along the road. On the light basemap, as a lone point, "closed" landed
 * as an anonymous black dot.
 */
export const CLOSURE_CONE_COLORS = {
    // Brighter than the line colours above: these sit on a dark badge, not on a pale map.
    closed: '#ff4d4d',
    restricted: '#ff9f43',
    hazardous: '#ffd166',
};
/**
 * How long a closure sits before its text goes out.
 *
 * Not a technical delay — a cancellation window. Somebody standing at a barricade
 * typing on a phone will mistype the road, drop the pin in the wrong place, or realise
 * it is the next road over. Five minutes means the corrected version is what the county
 * receives, and a closure deleted in that window texts nobody at all.
 */
export const CLOSURE_NOTIFY_DELAY_MIN = 5;
/** Days a reopened or expired closure is kept before Mongo sweeps it. */
export const CLOSURE_RETENTION_DAYS = 180;
/**
 * How long an unattended closure keeps claiming a road is shut.
 *
 * A closure entered and never cleared is the defining failure mode of every system like
 * this. One road wrongly shown as closed teaches a county to distrust the whole layer,
 * and they are right to. Expiring is the safer error: a road wrongly shown as OPEN is
 * still what the NWS warning and the map say it is, whereas a permanent phantom closure
 * is a lie the system tells indefinitely.
 */
export const CLOSURE_DEFAULT_TTL_HOURS = 24;
/** Most grace we will ever add after an operator's estimate. */
export const CLOSURE_REOPEN_GRACE_HOURS = 6;
/** Least, so even a fifteen-minute closure survives a slightly optimistic guess. */
export const CLOSURE_REOPEN_GRACE_MIN_HOURS = 1;
/**
 * How long a closure marked long-term stays up.
 *
 * Thirty days, not forever. A washed-out bridge or a months-long rebuild genuinely
 * outlives the 24-hour default, and dropping it off the map overnight would be wrong.
 * But "never expires" recreates the exact failure this whole mechanism exists to
 * prevent: a road shown as shut long after it reopened, with nobody left who remembers
 * entering it. Thirty days is past any storm response and still forces a human to
 * confirm it eventually — re-saving the closure resets the clock.
 */
export const CLOSURE_LONG_TERM_DAYS = 30;
/**
 * How long a closure is expected to last.
 *
 * Durations, not a date and time. The person entering this is standing at a barricade,
 * often on a phone, and they know "about an hour" — they do not know, and should not
 * have to compute, that it is 11:02. A `datetime-local` also makes the common case
 * (a short closure) the most work.
 */
export const CLOSURE_REOPEN_CHOICES = [
    { minutes: 15, label: '15 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 45, label: '45 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 120, label: '2 hours' },
    { minutes: 180, label: '3 hours' },
];
/**
 * How close a closure has to be to an asset to be worth mentioning.
 *
 * Deliberately NOT `asset.alertRadiusMiles`, which defaults to 10 miles and is tuned
 * for a storm polygon sweeping toward a building. Reusing it would report every closure
 * in the county as "near the courthouse", which is the same as reporting none.
 */
export const CLOSURE_NEAR_MILES = 2;
export function closureTtlMs(hours) {
    return hours * 3_600_000;
}
/**
 * The `autoExpireAt` a new or edited closure should carry.
 *
 * The grace is proportionate to the estimate rather than a flat six hours: twice the
 * expected duration, floored at an hour and capped at six. A fifteen-minute closure for
 * a crash should not keep claiming a road is shut until this evening — that is the
 * stale-closure failure mode this field exists to avoid — while an all-day event still
 * gets a sensible cushion.
 */
export function defaultAutoExpireAt(expectedReopenAt, now, longTerm = false) {
    // Long-term wins over any estimate: the two together mean "weeks, and here is a
    // guess at the next milestone", not "expire shortly after that guess".
    if (longTerm) {
        return new Date(now.getTime() + CLOSURE_LONG_TERM_DAYS * 86_400_000).toISOString();
    }
    if (expectedReopenAt) {
        const t = Date.parse(expectedReopenAt);
        if (Number.isFinite(t)) {
            const leadMs = Math.max(0, t - now.getTime());
            const graceMs = Math.min(closureTtlMs(CLOSURE_REOPEN_GRACE_HOURS), Math.max(closureTtlMs(CLOSURE_REOPEN_GRACE_MIN_HOURS), leadMs * 2));
            return new Date(t + graceMs).toISOString();
        }
    }
    return new Date(now.getTime() + closureTtlMs(CLOSURE_DEFAULT_TTL_HOURS)).toISOString();
}
/**
 * Should this closure be shown?
 *
 * Checked in application code rather than left to the Mongo TTL sweeper, which runs
 * about once a minute — the same reasoning as the short-link expiry in the API. A road
 * that has reopened must stop showing as shut on time, not within the minute.
 */
export function isClosureLive(c, now = Date.now()) {
    if (c.state !== 'active')
        return false;
    const t = Date.parse(c.autoExpireAt);
    // An unparseable expiry is treated as expired: a closure whose lifetime we cannot
    // reason about must not be the one that stays on screen forever.
    return Number.isFinite(t) && t > now;
}
/** Ordering for lists and for the map: worst condition first, then most recent. */
export function compareClosures(a, b) {
    const rank = (c) => CLOSURE_CONDITIONS.indexOf(c.condition);
    const d = rank(a) - rank(b);
    if (d !== 0)
        return d;
    return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
}
// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------
/** Miles from a point to the closure, whichever geometry it carries. */
export function closureDistanceMiles(g, point) {
    if (g.type === 'Point')
        return haversineMiles(point, g.coordinates);
    return pointToPathMiles(point, g.coordinates);
}
/**
 * Where to put a single marker for this closure.
 *
 * A point closure is its own anchor. A stretch of road gets the midpoint *by length*,
 * not the middle vertex: a freehand trace bunches vertices on the bends, so the middle
 * vertex can sit well off-centre — and on a two-point line it would be an endpoint.
 */
export function closureAnchor(g) {
    if (g.type === 'Point')
        return g.coordinates;
    const pts = g.coordinates;
    if (pts.length === 1)
        return pts[0];
    const legs = [];
    let total = 0;
    for (let i = 1; i < pts.length; i += 1) {
        const d = haversineMiles(pts[i - 1], pts[i]);
        legs.push(d);
        total += d;
    }
    // A zero-length line (every vertex identical) has no meaningful midpoint.
    if (total === 0)
        return pts[0];
    let walked = 0;
    for (let i = 0; i < legs.length; i += 1) {
        const leg = legs[i];
        if (walked + leg >= total / 2) {
            const t = leg === 0 ? 0 : (total / 2 - walked) / leg;
            const a = pts[i];
            const b = pts[i + 1];
            return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
        }
        walked += leg;
    }
    return pts[pts.length - 1];
}
/**
 * Bounding box, `[[minLon, minLat], [maxLon, maxLat]]`.
 *
 * Exists so nothing reaches for `boundsCenter` or `geomBounds`, which assume polygon
 * nesting and return nothing useful for a line. Null for an empty geometry.
 */
export function closureBounds(g) {
    const pts = g.type === 'Point' ? [g.coordinates] : g.coordinates;
    if (!pts.length)
        return null;
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const [lon, lat] of pts) {
        if (lon < minLon)
            minLon = lon;
        if (lat < minLat)
            minLat = lat;
        if (lon > maxLon)
            maxLon = lon;
        if (lat > maxLat)
            maxLat = lat;
    }
    return [
        [minLon, minLat],
        [maxLon, maxLat],
    ];
}
/**
 * Which of this county's assets are close enough to this closure to be worth naming.
 *
 * `CLOSURE_NEAR_MILES` is the cap; an asset configured tighter than that keeps its own
 * tighter radius, because someone who narrowed it did so on purpose.
 */
export function nearbyAssetsForClosure(geometry, assets, radiusMiles = CLOSURE_NEAR_MILES) {
    const out = [];
    for (const a of assets) {
        if (!a.enabled)
            continue;
        const limit = Math.min(radiusMiles, a.alertRadiusMiles || radiusMiles);
        const d = closureDistanceMiles(geometry, a.location.coordinates);
        if (d <= limit) {
            out.push({ assetId: a._id, name: a.name, type: a.type, distanceMiles: Math.round(d * 100) / 100 });
        }
    }
    return out.sort((x, y) => x.distanceMiles - y.distanceMiles);
}
export const CLOSURE_DRAFT_DEFAULT = {
    roadName: '',
    notes: '',
    reason: 'flood',
    condition: 'closed',
    geometry: null,
    expectedReopenAt: null,
    longTerm: false,
    notifyGroupIds: [],
};
const REASONS = new Set(CLOSURE_REASONS);
const CONDITIONS = new Set(CLOSURE_CONDITIONS);
/** A freehand trace of a winding county road, capped so one cannot be pathological. */
export const CLOSURE_MAX_VERTICES = 200;
function cleanGeometry(raw) {
    const g = raw;
    if (!g || typeof g !== 'object')
        return null;
    const ok = (c) => Array.isArray(c) &&
        c.length === 2 &&
        typeof c[0] === 'number' &&
        typeof c[1] === 'number' &&
        Number.isFinite(c[0]) &&
        Number.isFinite(c[1]) &&
        Math.abs(c[0]) <= 180 &&
        Math.abs(c[1]) <= 90;
    if (g.type === 'Point') {
        return ok(g.coordinates) ? { type: 'Point', coordinates: g.coordinates } : null;
    }
    if (g.type === 'LineString') {
        const cs = Array.isArray(g.coordinates) ? g.coordinates.filter(ok) : [];
        // Two points, or it is not a line. One tap must never reach the distance code as a
        // "line" — pointToPathMiles would read it as a degenerate segment.
        if (cs.length < 2)
            return null;
        return { type: 'LineString', coordinates: cs.slice(0, CLOSURE_MAX_VERTICES) };
    }
    return null;
}
/**
 * Coerce anything into a usable draft.
 *
 * One definition of "a valid closure", used by the API before storing AND by the form
 * before posting. The alternative — a hand-written field list on each side — is how the
 * alert-policy schema shipped silently dropping two fields on every save.
 */
export function cleanClosureInput(raw) {
    const c = (raw ?? {});
    const str = (v, max) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
    const when = typeof c.expectedReopenAt === 'string' && Number.isFinite(Date.parse(c.expectedReopenAt))
        ? new Date(c.expectedReopenAt).toISOString()
        : null;
    return {
        roadName: str(c.roadName, 120),
        notes: str(c.notes, 1000),
        reason: typeof c.reason === 'string' && REASONS.has(c.reason)
            ? c.reason
            : CLOSURE_DRAFT_DEFAULT.reason,
        condition: typeof c.condition === 'string' && CONDITIONS.has(c.condition)
            ? c.condition
            : CLOSURE_DRAFT_DEFAULT.condition,
        geometry: cleanGeometry(c.geometry),
        expectedReopenAt: when,
        longTerm: c.longTerm === true,
        // Ids only, deduped and bounded. Whether each id is a real group is the API's job;
        // an unknown id simply resolves to no recipients rather than to everyone.
        notifyGroupIds: Array.isArray(c.notifyGroupIds)
            ? [...new Set(c.notifyGroupIds.filter((g) => typeof g === 'string' && !!g))].slice(0, 20)
            : [],
    };
}
/** Null when the draft is postable; otherwise the reason it is not, for the form. */
export function closureDraftError(d) {
    if (!d.roadName)
        return 'Give the road a name — what you would say over the radio.';
    if (!d.geometry)
        return 'Mark the closure on the map: tap a point, or draw the closed stretch.';
    return null;
}
//# sourceMappingURL=closures.js.map